import axios, {
  getAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import type { GetServerSidePropsContext } from 'next'
import { axiosTransformResponse } from './api.utils'
import {
  logErrorCheckStatusInterceptor,
  logErrorInterceptor,
  logRequestInterceptor,
  logResponseInterceptor,
} from './interceptors/logs'

export default class ApiAxios {
  private readonly getEndpoint: () => string | undefined

  public axiosInstance: AxiosInstance

  constructor(getEndpoint: () => string | undefined, headers?: Record<string, string>) {
    this.axiosInstance = axios.create({
      baseURL: '', // baseURL is dynamically set in the get/put/post methods
      withCredentials: true,
      transformResponse: axiosTransformResponse,
    })
    this.getEndpoint = getEndpoint

    const defaultHeaders = {
      'Content-Type': 'application/json',
      // Prevent Cloudflare from blocking the default axios/1.x User-Agent
      'User-Agent': `card-prequal-ui/${process.env.npm_package_version || '1.0.0'}`,
    }

    this.axiosInstance.defaults.headers.common = { ...defaultHeaders, ...headers }
    this.axiosInstance.defaults.responseType = 'json'

    // add adapter when not testing environment
    // this fixes the issue with axios not working in the server when running on Vercel environment
    // works for local/vercel env client and server
    // but we don't want to use fetch adapter in tests
    if (process.env.NODE_ENV !== 'test') {
      this.axiosInstance.defaults.adapter = getAdapter('fetch')
    }

    //
    // Default request interceptors
    //

    this.axiosInstance.interceptors.request.use(logRequestInterceptor, logErrorInterceptor)

    //
    // Default response interceptors
    //

    this.axiosInstance.interceptors.response.use(
      logResponseInterceptor,
      logErrorCheckStatusInterceptor
    )
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    context?: GetServerSidePropsContext
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get(`${this.getEndpoint()}${url}`, {
      ...config,
      serverContext: context,
    })
  }

  public async post<T, U>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
    context?: GetServerSidePropsContext
  ): Promise<AxiosResponse<U>> {
    return this.axiosInstance.post(`${this.getEndpoint()}${url}`, data, {
      ...config,
      serverContext: context,
    })
  }

  public async put<T, U>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
    context?: GetServerSidePropsContext
  ): Promise<AxiosResponse<U>> {
    return this.axiosInstance.put(`${this.getEndpoint()}${url}`, data, {
      ...config,
      serverContext: context,
    })
  }

  // delete is not used in this project (yet)
}
